/**
 * Pure data helpers for the redesigned dashboard. Extracted from the component
 * so they are unit-testable without React. See docs/dashboard-redesign-plan.md.
 */
import type {
  Audit,
  AxisMatrixResponse,
  CitedPageEntry,
  LanguageScoreEntry,
} from '@traaker/shared';

export type DateRange = '7d' | '30d' | '90d' | '180d' | 'all';

export const RANGE_DAYS: Record<DateRange, number | null> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
  '180d': 180,
  all: null,
};

/** The GEO score blends the three brand axes (1/3 each) + website + earned media. */
export const GEO_WEIGHTS = { brandPerception: 0.4, website: 0.3, earnedMedia: 0.3 };
const BRAND_AXIS_WEIGHTS = { narrative: 1 / 3, authority: 1 / 3, referral: 1 / 3 };

export function computeBrandRollup(
  narrative: number | null,
  authority: number | null,
  referral: number | null,
): number | null {
  const parts: { value: number; weight: number }[] = [];
  if (narrative !== null) parts.push({ value: narrative, weight: BRAND_AXIS_WEIGHTS.narrative });
  if (authority !== null) parts.push({ value: authority, weight: BRAND_AXIS_WEIGHTS.authority });
  if (referral !== null) parts.push({ value: referral, weight: BRAND_AXIS_WEIGHTS.referral });
  if (parts.length === 0) return null;
  const totalWeight = parts.reduce((s, p) => s + p.weight, 0);
  return parts.reduce((s, p) => s + p.value * p.weight, 0) / totalWeight;
}

export function computeGeoScore(
  website: number | null,
  brand: number | null,
  earned: number | null,
): number | null {
  const parts: { value: number; weight: number }[] = [];
  if (website !== null) parts.push({ value: website, weight: GEO_WEIGHTS.website });
  if (brand !== null) parts.push({ value: brand, weight: GEO_WEIGHTS.brandPerception });
  if (earned !== null) parts.push({ value: earned, weight: GEO_WEIGHTS.earnedMedia });
  if (parts.length === 0) return null;
  const totalWeight = parts.reduce((sum, p) => sum + p.weight, 0);
  return parts.reduce((sum, p) => sum + p.value * p.weight, 0) / totalWeight;
}

export function filterByRange(audits: Audit[], range: DateRange): Audit[] {
  const days = RANGE_DAYS[range];
  if (!days) return audits;
  const cutoff = Date.now() - days * 86400000;
  return audits.filter((a) => new Date(a.createdAt).getTime() >= cutoff);
}

export function calcTrend(values: number[]): { delta: number; direction: 'up' | 'down' | 'flat' } {
  if (values.length < 2) return { delta: 0, direction: 'flat' };
  const last = values[values.length - 1];
  const prev = values[values.length - 2];
  const delta = Math.round(last - prev);
  return { delta, direction: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat' };
}

export interface SeriesPoint {
  /** ms epoch. */
  t: number;
  /** null = no data yet at this point. */
  value: number | null;
  /** True for the synthetic day-1 baseline (score 0 at brand creation). */
  baseline?: boolean;
}

/**
 * Build a chart series from completed audits, picking `value` per audit.
 *
 * Day-1 convention (validated): when there is exactly ONE real point, prepend a
 * synthetic `(brandCreatedAt, 0)` baseline so the line shows "départ → 1er
 * audit" instead of a lone dot. The baseline is only added when `brandCreatedAt`
 * predates the first audit. See docs/dashboard-redesign-plan.md (Day-1).
 */
export function buildSeries(
  audits: Audit[],
  pick: (a: Audit) => number | null | undefined,
  brandCreatedAt?: string,
): SeriesPoint[] {
  const points: SeriesPoint[] = [];
  for (const a of audits) {
    const v = pick(a);
    if (typeof v === 'number') points.push({ t: new Date(a.createdAt).getTime(), value: v });
  }
  points.sort((x, y) => x.t - y.t);

  if (points.length === 1 && brandCreatedAt) {
    const baseT = new Date(brandCreatedAt).getTime();
    if (Number.isFinite(baseT) && baseT < points[0].t) {
      return [{ t: baseT, value: 0, baseline: true }, ...points];
    }
  }
  return points;
}

/** Latest numeric value of a summary field across an axis's completed audits. */
export function latestScore(audits: Audit[], pick: (a: Audit) => number | null | undefined): number | null {
  for (let i = audits.length - 1; i >= 0; i -= 1) {
    const v = pick(audits[i]);
    if (typeof v === 'number') return v;
  }
  return null;
}

/** Sum per-engine citation counts across an axis's completed audits' latest rollup. */
export function latestEngineCitationCounts(audits: Audit[], pickSummary: (a: Audit) => { engine_citation_counts?: Record<string, number> } | undefined): Record<string, number> {
  for (let i = audits.length - 1; i >= 0; i -= 1) {
    const counts = pickSummary(audits[i])?.engine_citation_counts;
    if (counts && Object.keys(counts).length > 0) return counts;
  }
  return {};
}

/**
 * Merge per-engine citation counts across multiple axes for the same (latest)
 * audit cycle. Sums each engine across whichever axis summaries carry counts.
 */
export function mergeEngineCitationCounts(...countsList: Record<string, number>[]): Record<string, number> {
  const merged: Record<string, number> = {};
  for (const counts of countsList) {
    for (const [engine, n] of Object.entries(counts)) {
      merged[engine] = (merged[engine] || 0) + n;
    }
  }
  return merged;
}

/** Latest non-empty language_scores rollup across an axis's audits. */
export function latestLanguageScores(
  audits: Audit[],
  pickSummary: (a: Audit) => { language_scores?: LanguageScoreEntry[] } | undefined,
): LanguageScoreEntry[] {
  for (let i = audits.length - 1; i >= 0; i -= 1) {
    const ls = pickSummary(audits[i])?.language_scores;
    if (ls && ls.length > 0) return ls;
  }
  return [];
}

/** Latest non-empty cited_pages rollup across an axis's audits. */
export function latestCitedPages(
  audits: Audit[],
  pickSummary: (a: Audit) => { cited_pages?: CitedPageEntry[] } | undefined,
): CitedPageEntry[] {
  for (let i = audits.length - 1; i >= 0; i -= 1) {
    const cp = pickSummary(audits[i])?.cited_pages;
    if (cp && cp.length > 0) return cp;
  }
  return [];
}

export interface CompetitorRankRow {
  name: string;
  /** Share-of-voice %: presentOn / promptCount. null when no data. */
  pct: number | null;
  isBrand: boolean;
}

/**
 * Competitor ranking ("% vues") from the latest referral matrix audit.
 * Mirrors CompetitorMatrixView: competitors ranked by referral
 * presentOn/promptCount; the brand's own % comes from its per-prompt
 * queryScores (recommended/ranked share over total prompts), so the brand row
 * is comparable and appears highlighted in the list.
 */
export function competitorRanking(
  matrix: AxisMatrixResponse | null,
  brandName: string,
): CompetitorRankRow[] {
  const latest = matrix?.audits?.[0];
  const footer = latest?.competitorMatrix?.footer;
  if (!footer) return [];
  const rows: CompetitorRankRow[] = Object.entries(footer).map(([name, f]) => ({
    name,
    pct: f && f.promptCount ? Math.round((f.presentOn / f.promptCount) * 100) : null,
    isBrand: false,
  }));

  // Brand's own % vues from queryScores (referral: recommended or ranked).
  const qs = latest?.queryScores || [];
  if (qs.length > 0) {
    const present = qs.filter((e) => e.bestPosition || (e.recommendedRate ?? 0) > 0).length;
    rows.push({
      name: brandName,
      pct: Math.round((present / qs.length) * 100),
      isBrand: true,
    });
  }

  rows.sort((a, b) => (b.pct ?? -1) - (a.pct ?? -1));
  return rows;
}
